import { useEffect, useState, useRef } from "react";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Save, Award, Upload, Trash2, Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  signature_name: "",
  signature_role: "",
  signature_company: "",
  body_text: "",
  stamp_label: "",
  footer_text: "",
};

const COPY = {
  certificate: {
    title: "Certificado",
    docNoun: "certificado",
    intro: "Personaliza el texto del certificado: firma, cargo del responsable, pie y sello.",
    bodyPlaceholder: "por haber superado satisfactoriamente el curso",
    bodyHelp: "Aparece justo encima del nombre del curso en el certificado.",
    stampPlaceholder: "APROBADO",
    footerPlaceholder: "laclaseencasa · La plataforma de formación de SIMA Asistencial SL",
    previewTitle: "Vista previa del certificado",
  },
  diploma: {
    title: "Diploma",
    docNoun: "diploma",
    intro: "Configura el diploma que se entrega cuando un alumno aprueba un examen final.",
    bodyPlaceholder: "ha completado con éxito todas las pruebas finales del programa",
    bodyHelp: "Aparece justo encima del nombre del programa en el diploma.",
    stampPlaceholder: "EXAMEN APROBADO",
    footerPlaceholder: "Diploma oficial expedido por SIMA Asistencial SL · laclaseencasa",
    previewTitle: "Vista previa del diploma",
  },
};

export default function DocumentSettingsPanel({ kind = "certificate" }) {
  const base = `/admin/settings/${kind}`;
  const copy = COPY[kind];

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [sigBuster, setSigBuster] = useState(Date.now());
  const fileRef = useRef();
  const [sigRemoveBg, setSigRemoveBg] = useState(false);
  const [hasStamp, setHasStamp] = useState(false);
  const [stampBuster, setStampBuster] = useState(Date.now());
  const stampFileRef = useRef();
  const [stampRemoveBg, setStampRemoveBg] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoBuster, setLogoBuster] = useState(Date.now());
  const logoFileRef = useRef();
  const [logoRemoveBg, setLogoRemoveBg] = useState(false);
  const [previewBuster, setPreviewBuster] = useState(Date.now());
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sigPreview, setSigPreview] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Load on mount or when kind changes (reset state)
  useEffect(() => {
    setForm(EMPTY);
    setSigPreview(null);
    setStampPreview(null);
    setLogoPreview(null);
    setPreviewUrl(null);
    Promise.all([
      api.get(base),
      api.get(`${base}/signature`),
      api.get(`${base}/stamp`),
      api.get(`${base}/logo`),
    ]).then(([s, sig, st, lg]) => {
      setForm({ ...EMPTY, ...s.data });
      setHasSignature(!!sig.data.present);
      setHasStamp(!!st.data.present);
      setHasLogo(!!lg.data.present);
      setSigBuster(Date.now());
      setStampBuster(Date.now());
      setLogoBuster(Date.now());
      setPreviewBuster(Date.now());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const refreshPreview = () => setPreviewBuster(Date.now());

  const save = async () => {
    setSaving(true);
    try {
      await api.put(base, form);
      toast.success(`Ajustes del ${copy.docNoun} guardados`);
      refreshPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (e, kindOf, setHas, setBuster, removeBg) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`${base}/${kindOf}?remove_bg=${removeBg}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(removeBg ? "Imagen subida (fondo eliminado)" : "Imagen subida");
      setHas(true);
      setBuster(Date.now());
      refreshPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    e.target.value = "";
  };

  const deleteImage = async (kindOf, setHas, setBuster, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    await api.delete(`${base}/${kindOf}`);
    toast.success("Imagen eliminada");
    setHas(false);
    setBuster(Date.now());
    refreshPreview();
  };

  // Authenticated image previews
  useEffect(() => {
    if (!hasSignature) {
      setSigPreview(null);
      return;
    }
    const token = localStorage.getItem("token");
    fetch(`${API}${base}/signature/file?t=${sigBuster}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) setSigPreview(URL.createObjectURL(blob));
      });
  }, [hasSignature, sigBuster, base]);

  useEffect(() => {
    if (!hasStamp) {
      setStampPreview(null);
      return;
    }
    const token = localStorage.getItem("token");
    fetch(`${API}${base}/stamp/file?t=${stampBuster}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) setStampPreview(URL.createObjectURL(blob));
      });
  }, [hasStamp, stampBuster, base]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API}${base}/logo/file?t=${logoBuster}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) setLogoPreview(URL.createObjectURL(blob));
        else setLogoPreview(null);
      });
  }, [hasLogo, logoBuster, base]);

  // PDF live preview
  useEffect(() => {
    let active = true;
    let url = null;
    setPreviewLoading(true);
    const token = localStorage.getItem("token");
    fetch(`${API}${base}/preview?t=${previewBuster}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!active) return;
        if (blob) {
          url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        }
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewBuster, base]);

  return (
    <div data-testid={`document-settings-${kind}`}>
      <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-500">{copy.intro}</p>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-brand hover:bg-brand-hover text-white"
          data-testid={`save-settings-${kind}`}
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-5 max-w-3xl">
        <div className="flex items-center gap-2 text-brand-ink">
          <Award className="w-5 h-5" />
          <h2 className="font-display text-lg">{copy.title}</h2>
        </div>

        <div>
          <Label htmlFor={`body_text_${kind}`}>Texto principal del {copy.docNoun}</Label>
          <Textarea
            id={`body_text_${kind}`}
            value={form.body_text}
            onChange={(e) => update("body_text", e.target.value)}
            placeholder={copy.bodyPlaceholder}
            data-testid={`body-text-input-${kind}`}
          />
          <p className="text-xs text-gray-500 mt-1">{copy.bodyHelp}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-100">
          <div>
            <Label htmlFor={`signature_name_${kind}`}>Firma (nombre)</Label>
            <Input
              id={`signature_name_${kind}`}
              value={form.signature_name}
              onChange={(e) => update("signature_name", e.target.value)}
              placeholder="Ej. Ana Martín"
              data-testid={`signature-name-input-${kind}`}
            />
          </div>
          <div>
            <Label htmlFor={`signature_role_${kind}`}>Cargo del responsable</Label>
            <Input
              id={`signature_role_${kind}`}
              value={form.signature_role}
              onChange={(e) => update("signature_role", e.target.value)}
              placeholder="Ej. Director de formación"
              data-testid={`signature-role-input-${kind}`}
            />
          </div>
        </div>

        <div>
          <Label htmlFor={`signature_company_${kind}`}>Empresa</Label>
          <Input
            id={`signature_company_${kind}`}
            value={form.signature_company}
            onChange={(e) => update("signature_company", e.target.value)}
            placeholder="SIMA Asistencial SL"
            data-testid={`signature-company-input-${kind}`}
          />
          <p className="text-xs text-gray-500 mt-1">Aparece junto al cargo y en el sello oficial.</p>
        </div>

        {/* Signature image */}
        <ImageUploadBlock
          title="Firma manuscrita (imagen)"
          help={`Sube una imagen PNG con fondo transparente (recomendado) o JPG. Reemplaza al texto de la firma en el ${copy.docNoun}. Máx. 5 MB.`}
          present={hasSignature}
          previewUrl={sigPreview}
          previewAlt="Firma"
          emptyText="Sin firma cargada"
          uploadLabel={hasSignature ? "Reemplazar firma" : "Subir firma"}
          fileRef={fileRef}
          onFileChange={(e) => uploadImage(e, "signature", setHasSignature, setSigBuster, sigRemoveBg)}
          onRemove={() => deleteImage("signature", setHasSignature, setSigBuster, "¿Eliminar la firma? Se usará el nombre como texto.")}
          removeBg={sigRemoveBg}
          setRemoveBg={setSigRemoveBg}
          testIdPrefix={`signature-${kind}`}
        />

        {/* Stamp image */}
        <ImageUploadBlock
          title="Sello (imagen)"
          help={`Sube una imagen PNG con fondo transparente (recomendado) o JPG. Sustituye al sello circular generado en el ${copy.docNoun}. Máx. 5 MB.`}
          present={hasStamp}
          previewUrl={stampPreview}
          previewAlt="Sello"
          previewClass="h-20 w-20"
          emptyText="Sin sello cargado"
          uploadLabel={hasStamp ? "Reemplazar sello" : "Subir sello"}
          fileRef={stampFileRef}
          onFileChange={(e) => uploadImage(e, "stamp", setHasStamp, setStampBuster, stampRemoveBg)}
          onRemove={() => deleteImage("stamp", setHasStamp, setStampBuster, "¿Eliminar el sello? Se usará el sello generado por defecto.")}
          removeBg={stampRemoveBg}
          setRemoveBg={setStampRemoveBg}
          testIdPrefix={`stamp-${kind}`}
        />

        {/* Logo */}
        <ImageUploadBlock
          title={`Logo cabecera del ${copy.docNoun}`}
          help="Sustituye al logo por defecto en la cabecera del PDF. PNG (recomendado con transparencia) o JPG. Máx. 5 MB."
          present={hasLogo}
          previewUrl={logoPreview}
          previewAlt="Logo"
          previewClass="h-20"
          emptyText="Sin logo cargado"
          uploadLabel={hasLogo ? "Reemplazar logo" : "Subir logo"}
          fileRef={logoFileRef}
          onFileChange={(e) => uploadImage(e, "logo", setHasLogo, setLogoBuster, logoRemoveBg)}
          onRemove={() => deleteImage("logo", setHasLogo, setLogoBuster, "¿Eliminar el logo personalizado? Se usará el logo por defecto.")}
          removeBg={logoRemoveBg}
          setRemoveBg={setLogoRemoveBg}
          testIdPrefix={`logo-${kind}`}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-100">
          <div>
            <Label htmlFor={`stamp_label_${kind}`}>Texto del sello</Label>
            <Input
              id={`stamp_label_${kind}`}
              value={form.stamp_label}
              onChange={(e) => update("stamp_label", e.target.value.toUpperCase())}
              placeholder={copy.stampPlaceholder}
              maxLength={20}
              className="font-mono-data"
              data-testid={`stamp-label-input-${kind}`}
            />
          </div>
          <div>
            <Label htmlFor={`footer_text_${kind}`}>Mensaje de pie de {copy.docNoun}</Label>
            <Input
              id={`footer_text_${kind}`}
              value={form.footer_text}
              onChange={(e) => update("footer_text", e.target.value)}
              placeholder={copy.footerPlaceholder}
              data-testid={`footer-text-input-${kind}`}
            />
          </div>
        </div>
      </Card>

      <Card
        className="mt-6 p-6 border border-gray-200 shadow-none rounded-lg max-w-3xl"
        data-testid={`${kind}-preview-card`}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2 text-brand-ink">
            <Eye className="w-5 h-5" />
            <h2 className="font-display text-lg">{copy.previewTitle}</h2>
          </div>
          <div className="flex gap-2 flex-wrap">
            {previewUrl ? (
              <Button type="button" variant="outline" asChild>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`open-preview-new-tab-${kind}`}
                >
                  Abrir en pestaña nueva
                </a>
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={refreshPreview}
              disabled={previewLoading}
              data-testid={`refresh-preview-button-${kind}`}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${previewLoading ? "animate-spin" : ""}`} />
              Actualizar previsualización
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Se genera un PDF de muestra con los ajustes actuales (alumno, curso y fecha ficticios).
          Pulsa <span className="font-medium">Actualizar</span> tras cambiar imágenes o textos para regenerarlo.
        </p>
        <div className="border border-gray-200 rounded-md overflow-hidden bg-gray-50" style={{ height: 520 }}>
          {previewUrl ? (
            <object
              data={previewUrl}
              type="application/pdf"
              className="w-full h-full"
              data-testid={`${kind}-preview-pdf`}
            >
              <p className="text-xs text-gray-500 p-4">
                Tu navegador no puede mostrar el PDF embebido.{" "}
                <a href={previewUrl} target="_blank" rel="noreferrer" className="underline text-brand">
                  Ábrelo en una pestaña nueva
                </a>
                .
              </p>
            </object>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
              {previewLoading ? "Generando vista previa…" : "Sin vista previa"}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function ImageUploadBlock({
  title,
  help,
  present,
  previewUrl,
  previewAlt,
  previewClass = "h-16",
  emptyText,
  uploadLabel,
  fileRef,
  onFileChange,
  onRemove,
  removeBg,
  setRemoveBg,
  testIdPrefix,
}) {
  return (
    <div className="pt-4 border-t border-gray-100">
      <Label className="text-sm font-medium text-brand-ink">{title}</Label>
      <p className="text-xs text-gray-500 mt-1 mb-3">{help}</p>
      <input
        ref={fileRef}
        type="file"
        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
        onChange={onFileChange}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      <div className="flex items-center gap-4 flex-wrap">
        {previewUrl ? (
          <div className="border border-gray-200 rounded-md p-3 bg-[#f8fbfa]">
            <img
              src={previewUrl}
              alt={previewAlt}
              className={`${previewClass} object-contain`}
              style={{ mixBlendMode: "multiply" }}
            />
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 rounded-md px-6 py-4 text-xs text-gray-400">
            {emptyText}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            data-testid={`${testIdPrefix}-upload-button`}
          >
            <Upload className="w-4 h-4 mr-1" />
            {uploadLabel}
          </Button>
          {present && (
            <Button
              type="button"
              variant="ghost"
              onClick={onRemove}
              data-testid={`${testIdPrefix}-remove-button`}
            >
              <Trash2 className="w-4 h-4 mr-1 text-red-500" />
              Quitar
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Checkbox
          id={`${testIdPrefix}-remove-bg`}
          checked={removeBg}
          onCheckedChange={(v) => setRemoveBg(v === true)}
          data-testid={`${testIdPrefix}-remove-bg`}
        />
        <Label htmlFor={`${testIdPrefix}-remove-bg`} className="text-xs text-gray-600 cursor-pointer font-normal">
          Quitar fondo blanco al subir (transparencia automática)
        </Label>
      </div>
    </div>
  );
}
